import { getAuthToken, getCurrentUser } from '../services/authService';

/**
 * Role definitions for the application
 */
export enum UserRole {
  SYSTEM_ADMIN = 'SystemAdmin',
  SALES_REP = 'SalesRep',
  CASE_MANAGER = 'CaseManager',
  SECRETARY = 'Secretary',
  TENANT_ADMIN = 'TenantAdmin'
}

/**
 * Check if the current user has any of the required roles
 * @param requiredRoles Array of roles to check against
 * @returns boolean indicating if user has at least one of the required roles
 */
export const hasRole = (requiredRoles: string[]): boolean => {
  const token = getAuthToken();
  if (!token) return false;
  
  try {
    // Decode JWT token payload
    const base64Payload = token.split('.')[1];
    const payload = JSON.parse(atob(base64Payload));
    
    // Check for role claim in different possible claim names
    const userRole = payload.role || 
                    payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ||
                    payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/role'];
    
    if (userRole) {
      return requiredRoles.includes(userRole);
    }
  } catch (error) {
    console.warn('Failed to decode JWT token, falling back to localStorage user data');
  }
  
  // Fallback to localStorage user data
  const user = getCurrentUser();
  if (!user) return false;
  
  // Check against permission level name
  return requiredRoles.includes(user.permissionLevelName);
};

/**
 * Check if the current user is a system administrator
 * @returns boolean indicating if user is system admin
 */
export const isSystemAdmin = (): boolean => {
  return hasRole([UserRole.SYSTEM_ADMIN]);
};

/**
 * Check if the current user is a sales representative
 * @returns boolean indicating if user is sales rep
 */
export const isSalesRep = (): boolean => {
  return hasRole([UserRole.SALES_REP]);
};

/**
 * Check if the current user is a case manager
 * @returns boolean indicating if user is case manager
 */
export const isCaseManager = (): boolean => {
  return hasRole([UserRole.CASE_MANAGER]);
};

/**
 * Check if the current user is a secretary
 * @returns boolean indicating if user is secretary
 */
export const isSecretary = (): boolean => {
  return hasRole([UserRole.SECRETARY]);
};

/**
 * Check if the current user is a tenant administrator (manages parameters only)
 * @returns boolean indicating if user is tenant admin
 */
export const isTenantAdmin = (): boolean => {
  return hasRole([UserRole.TENANT_ADMIN]);
};

/**
 * Check if the current user has supervisor privileges
 * @returns boolean indicating if user is supervisor
 */
export const isSupervisor = (): boolean => {
  const user = getCurrentUser();
  return user ? user.isSupervisor : false;
};

/**
 * Get the current user's role name
 * @returns string role name or null if not authenticated
 */
export const getCurrentUserRole = (): string | null => {
  const token = getAuthToken();
  if (!token) return null;
  
  try {
    // Try to get role from JWT token first
    const base64Payload = token.split('.')[1];
    const payload = JSON.parse(atob(base64Payload));
    
    const userRole = payload.role || 
                    payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ||
                    payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/role'];
    
    if (userRole) {
      return userRole;
    }
  } catch (error) {
    console.warn('Failed to decode JWT token, falling back to localStorage user data');
  }
  
  // Fallback to localStorage user data
  const user = getCurrentUser();
  return user ? user.permissionLevelName : null;
};

/**
 * Get the current user's ID
 * @returns number user ID or null if not authenticated
 */
export const getCurrentUserId = (): number | null => {
  const user = getCurrentUser();
  return user ? user.id : null;
};